import { useState } from 'react';
import { Plus, Question } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { QuestionCard } from './QuestionCard';
import { QuestionWizardDialog } from 'components/dueDiligence/QuestionWizard';
import type { QuestionItem, AnswerOptionItem } from './types';
import type { ConditionGroup } from 'shared/types/due-diligence';
import type { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';

interface QuestionsTabProps {
  questions: QuestionItem[];
  expandedQuestions: Set<number>;
  fileLabels: DatabaseFileLabel[];
  conditionsClipboard: ConditionGroup | null;
  onAddQuestion: (question?: QuestionItem) => void;
  onUpdateQuestion: (index: number, updates: Partial<QuestionItem>) => void;
  onRemoveQuestion: (index: number) => void;
  onMoveQuestion: (index: number, direction: 'up' | 'down') => void;
  onDuplicateQuestion: (index: number) => void;
  toggleQuestionExpanded: (questionId: number) => void;
  onAddAnswerOption: (questionIndex: number) => void;
  onUpdateAnswerOption: (questionIndex: number, optionIndex: number, updates: Partial<AnswerOptionItem>) => void;
  onRemoveAnswerOption: (questionIndex: number, optionIndex: number) => void;
  onCopyConditions: (questionIndex: number, optionIndex: number) => void;
  onPasteConditions: (questionIndex: number, optionIndex: number) => void;
  onOpenDocumentLabelPicker: (questionIndex: number) => void;
}

export const QuestionsTab: React.FC<QuestionsTabProps> = ({
  questions,
  expandedQuestions,
  fileLabels,
  conditionsClipboard,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
  onMoveQuestion,
  onDuplicateQuestion,
  toggleQuestionExpanded,
  onAddAnswerOption,
  onUpdateAnswerOption,
  onRemoveAnswerOption,
  onCopyConditions,
  onPasteConditions,
  onOpenDocumentLabelPicker,
}) => {
  const [wizardOpen, setWizardOpen] = useState(false);
  const sortedQuestions = [...questions].sort((a, b) => a.displayOrder - b.displayOrder);

  const handleWizardComplete = (question: QuestionItem) => {
    onAddQuestion(question);
  };

  return (
    <div className="space-y-4" data-tour="dd-questions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">Controle Vragen</h3>
          <p className="text-sm text-muted-foreground">Vragen met antwoordopties en status kleuren</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setWizardOpen(true)} data-tour="dd-add-question">
          <Plus className="h-4 w-4 mr-1" />
          Vraag toevoegen
        </Button>
      </div>

      {questions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Question className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">Nog geen vragen</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm">
              Voeg vragen toe zoals: &quot;Is de rookmelder aanwezig?&quot;, &quot;Is het EPC certificaat
              geldig?&quot; Elke vraag heeft antwoordopties met kleuren.
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => setWizardOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Eerste vraag toevoegen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-tour="dd-question-list">
          {sortedQuestions.map((question, qIndex) => {
            const isExpanded = question.id ? expandedQuestions.has(question.id) : true;
            return (
              <QuestionCard
                key={question.id || `q-${qIndex}`}
                question={question}
                questionIndex={qIndex}
                isExpanded={isExpanded}
                isFirst={qIndex === 0}
                isLast={qIndex === questions.length - 1}
                fileLabels={fileLabels}
                questions={questions}
                conditionsClipboard={conditionsClipboard}
                onToggleExpanded={() => question.id && toggleQuestionExpanded(question.id)}
                onUpdate={onUpdateQuestion}
                onRemove={onRemoveQuestion}
                onMove={onMoveQuestion}
                onDuplicate={onDuplicateQuestion}
                onAddAnswerOption={onAddAnswerOption}
                onUpdateAnswerOption={onUpdateAnswerOption}
                onRemoveAnswerOption={onRemoveAnswerOption}
                onCopyConditions={onCopyConditions}
                onPasteConditions={onPasteConditions}
                onOpenDocumentLabelPicker={onOpenDocumentLabelPicker}
              />
            );
          })}
        </div>
      )}

      <QuestionWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onComplete={handleWizardComplete}
        existingQuestions={questions}
      />
    </div>
  );
};
